/**
 * TurbinePhotoGallery - Galleria foto turbina con upload remoto
 *
 * Features:
 * - Visualizzazione foto esistenti (tipologie: accettazione, lavorazione, completamento)
 * - Upload foto via RemoteCameraMultiPhoto (CORE component)
 * - Lightbox per visualizzazione full-screen
 * - Filtro per tipologia foto
 */

import { useState, useEffect } from 'react';
import { Camera, X, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { RemoteCameraMultiPhoto } from '../../../core/components/PhotoCapture';
import { appConfirm } from '../../../lib/appDialog';

interface TurbinePhoto {
  id: string;
  photo_url: string;
  thumbnail_url?: string;
  uploaded_at: string;
  context_type: string;
  comment?: string;
}

interface TurbinePhotoGalleryProps {
  turbineId: string;
  userId: string;
  readonly?: boolean;
}

const PHOTO_TYPES = {
  'turbine-acceptance': { label: '📥 Accettazione', color: 'blue' },
  'turbine-damage': { label: '🔧 Lavorazione', color: 'yellow' },
  'turbine-completion': { label: '✅ Completamento', color: 'green' }
};

type PhotoType = keyof typeof PHOTO_TYPES;

function TurbinePhotoGallery({ turbineId, userId, readonly = false }: TurbinePhotoGalleryProps) {
  const [photos, setPhotos] = useState<TurbinePhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedType, setSelectedType] = useState<PhotoType>('turbine-acceptance');
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<PhotoType | 'all'>('all');

  useEffect(() => {
    fetchPhotos();
  }, [turbineId]);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      // Cerca tutte le sessioni foto per questa turbina
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/photo-sessions/by-context`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextType: 'turbine',
            contextId: turbineId
          })
        }
      );

      if (!response.ok) {
        console.error('Errore caricamento foto:', response.statusText);
        setPhotos([]);
        return;
      }

      const data = await response.json();
      if (data.success && data.photos) {
        setPhotos(data.photos);
      } else {
        setPhotos([]);
      }
    } catch (error) {
      console.error('Errore fetch foto:', error);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoDelete = async (photoId: string) => {
    if (!(await appConfirm('Eliminare questa foto?'))) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/photo-sessions/photo/${photoId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setPhotos(photos.filter(p => p.id !== photoId));
      }
    } catch (error) {
      console.error('Errore eliminazione foto:', error);
    }
  };

  const filteredPhotos = filterType === 'all'
    ? photos
    : photos.filter(p => p.context_type === filterType);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-400">Caricamento foto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
          <Camera className="w-5 h-5 text-blue-400" />
          Foto Turbina ({filteredPhotos.length})
        </h2>

        {!readonly && (
          <button
            onClick={() => setShowUpload(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi foto
          </button>
        )}
      </div>

      {/* Filtri tipologia */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFilterType('all')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            filterType === 'all'
              ? 'bg-blue-500/20 text-blue-300 border border-blue-500'
              : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
          }`}
        >
          Tutte ({photos.length})
        </button>
        {Object.entries(PHOTO_TYPES).map(([type, config]) => {
          const count = photos.filter(p => p.context_type === type).length;
          return (
            <button
              key={type}
              onClick={() => setFilterType(type as PhotoType)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filterType === type
                  ? `bg-${config.color}-500/20 text-${config.color}-300 border border-${config.color}-500`
                  : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
              }`}
            >
              {config.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Galleria foto */}
      {filteredPhotos.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="mb-4">
            {filterType === 'all'
              ? 'Nessuna foto caricata'
              : `Nessuna foto di tipo ${PHOTO_TYPES[filterType]?.label}`}
          </p>
          {!readonly && (
            <button
              onClick={() => setShowUpload(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Carica prima foto
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPhotos.map((photo) => (
            <div
              key={photo.id}
              className="relative group aspect-square bg-gray-900 rounded-lg overflow-hidden border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => setLightboxPhoto(photo.photo_url)}
            >
              <img
                src={photo.thumbnail_url || photo.photo_url}
                alt={`Foto ${photo.context_type}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />

              {/* Overlay con info */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-xs text-white font-medium mb-1">
                    {PHOTO_TYPES[photo.context_type as PhotoType]?.label || photo.context_type}
                  </div>
                  <div className="text-xs text-gray-300">
                    {new Date(photo.uploaded_at).toLocaleDateString('it-IT')}
                  </div>
                </div>

                {!readonly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePhotoDelete(photo.id);
                    }}
                    className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Upload Foto */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-100">Carica Foto Turbina</h3>
              <button
                onClick={() => setShowUpload(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Selezione tipologia foto */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Tipologia foto
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(PHOTO_TYPES).map(([type, config]) => (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type as PhotoType)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        selectedType === type
                          ? `border-${config.color}-500 bg-${config.color}-500/20`
                          : 'border-gray-600 hover:border-gray-500 bg-gray-900'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">{config.label.split(' ')[0]}</div>
                        <div className={`text-sm font-medium ${
                          selectedType === type ? `text-${config.color}-300` : 'text-gray-400'
                        }`}>
                          {config.label.split(' ').slice(1).join(' ')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Remote Camera Component */}
              <RemoteCameraMultiPhoto
                turbineId={turbineId}
                damageTypeId={0} // Non usato per questo contesto
                userId={userId}
                onPhotosUpdate={(newPhotos) => {
                  console.log('Foto aggiornate:', newPhotos);
                  fetchPhotos(); // Ricarica galleria
                }}
              />

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowUpload(false);
                    fetchPhotos();
                  }}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  Fatto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxPhoto}
            alt="Foto full screen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default TurbinePhotoGallery;
